import { DeliveryPromiseStoreProduct } from './DeliveryPromise';
import { ImagePathWithRenditions } from './ImageTypes';
import { SocialMediaLinkProps } from './SocialTypes';

export type ClickAndCollectSortBy = 'storeName' | 'distance';

export type ClickAndCollectStore = {
  storeId: number;
  storeName: string;
  storeNumber: number;
  deliveryFrom?: number;
  deliveryTo?: number;
  deliveryUnit?: string;
  distance: number;
  distanceUnit?: string;
  quantity?: number;
  available: boolean;
  availableInSSEStore?: boolean;
  orderLinesHaveDifferentPromises?: unknown;
  productPromises?: Array<DeliveryPromiseStoreProduct>;
  deliveryMessage?: string;
  ultimateDispatchDay?: number;
  hasDeliveryMessage: boolean;
  longitude: string;
  latitude: string;
  itemsCount?: number;
  inStock?: boolean;
  fasciaCode: string;
  fasciaId: number;
  address?: StoreAddress;
  scheduleEntries?: ScheduleEntries;
  specialtyStore?: boolean;
  selectable?: boolean;
};

export type ClickAndCollectStoreProduct = {
  skuId: number;
  productId: number;
  colorId: number;
  quantity: number;
};

export type Store = {
  active: boolean;
  alias: string;
  brands: Array<string>;
  businessUnit: string;
  city: string;
  cleanOpeningHours: Array<StoreOpeningHour>;
  code: string;
  coordinates: string;
  country: string;
  dailyOpeningHours: Array<Array<StoreOpeningHour>>;
  departments: Array<StoreDepartment>;
  description: string;
  exceptionalOpeningHours: Array<StoreOpeningHour>;
  googleMapsIconForBusinessUnit: string;
  latitude: string;
  longitude: string;
  mainWebShop: string;
  name: string;
  newStore: boolean;
  onWeb: boolean;
  openSunday: boolean;
  phoneNumber: string;
  postalcode: string;
  relativeStorePagePath: string;
  sortingFirstLetter: string;
  street: string;
  visible: boolean;
};

export type StoreOpeningHour = {
  close: number;
  closeTimeInMinutes: number;
  closeTimeLabel: string;
  date: number;
  day: string;
  open: number;
  openTimeInMinutes: number;
  openTimeLabel: string;
};

export type StoreDepartment = {
  brands: Array<string>;
  code: string;
  description: string;
};

export type ProductCountPerStore = {
  storeId: number;
  itemCount: number;
};

type StoreTranslatedProperties = {
  languageCode: string;
  description: string;
  webDescription: string;
};

export type ShoplandingInfo = {
  storeId: number;
  storeTranslatedProperties: Record<string, StoreTranslatedProperties>;
  geographic: StoreGeographic;
};

export type StoreAddress = {
  street: string;
  // eslint-disable-next-line
  number: string;
  bus?: string;
  zipCode: string;
  city: string;
  country: string;
};

export type StoreGeographic = {
  gpsCoordinates: string;
  latitude: string;
  longitude: string;
  streetViewLatitude?: string;
  streetViewLongitude?: string;
};

export type OpenedStore = {
  city: string;
  storeCode: string;
  country: string;
  mainWebShop: string;
  storeName: string;
  postalCode: string;
  street: string;
  relativeStorePagePath: string;
  image: ImagePathWithRenditions;
};

export type StoreInformation = {
  address: StoreAddress;
  phoneNumber: string;
  scheduleEntries: Array<ScheduleEntries>;
  storeName: string;
  code: number;
  description: string;
  newShop: boolean;
  geographic: StoreGeographic;
  fasciaCode: string;
  fasciaId: number;
};

export type ScheduleEntries = {
  closeTimeInMinutes: number;
  closed: boolean;
  closingHour: string;
  date: string;
  isExceptional: boolean;
  openTimeInMinutes: number;
  openTimeInSeconds?: number;
  closeTimeInSeconds?: number;
  openingHour: string;
  day: string;
};

export type StoreInformationWithPagePathAndImage = StoreInformation & {
  relativeStorePagePath: string;
  image?: ImagePathWithRenditions;
};

export type StoresPagePathAndImageByStore = {
  code: string;
  relativeStorePagePath: string;
  image?: ImagePathWithRenditions;
};

export type StoreGeneralProps = StoreInformation & {
  image?: ImagePathWithRenditions;
  googleMapsLink?: string;
  socialMediaLinks?: Array<SocialMediaLinkProps>;
  storeCode: string;
};

export type StoreNearbyStoresProps = {
  nearbyStoreList: Array<OpenedStore>;
};

export type StoreRecentlyOpenedStoresProps = {
  dataUrl: string;
};
